import { humanize } from '../../shared/labels';
import {
  BaselineLayer,
  BaselineStackStatus,
  type AccountBaselineView,
  type BaselineLayerType,
  type BaselineStackView,
} from '../../types/backend';
import { stackReason } from './deriveOnboardingGates';

/**
 * One baseline's state, rolled up from its stacks. The keys are StatusChip's.
 *
 * There is no Outdated and no Failed: the status read records neither, and a
 * refusal or a block is a halt waiting on a person, which Refused and Blocked
 * say with their reason.
 */
export const BaselineHealth = {
  IN_SYNC: 'IN_SYNC',
  APPLYING: 'APPLYING',
  MISSING: 'MISSING',
  REFUSED: 'REFUSED',
  BLOCKED: 'BLOCKED',
  WAITING_ON_DEPENDENCY: 'WAITING_ON_DEPENDENCY',
  OFF: 'OFF',
} as const;

export type BaselineHealthType = typeof BaselineHealth[keyof typeof BaselineHealth];

/**
 * The backend's blocked reasons that are a wait for another baseline rather
 * than a fault. The status read already reports such a stack as PENDING; this
 * says which PENDING is a wait. Not a generated enum on the backend, so named
 * once here.
 */
const WAITING_REASONS = new Set(['WAITING_FOR_BASELINE', 'WAITING_FOR_ACCESS_ROLES']);

const TITLES: Record<string, string> = {
  access: 'Access',
  signals: 'Signals',
  data: 'Data',
  finops: 'FinOps',
};

/** One region a baseline is expected in, and the state its stacks there are in. */
export interface BaselinePlacement {
  region: string;
  main: boolean;
  health: BaselineHealthType;
}

export interface BaselineSummary {
  name: string;
  title: string;
  layer: BaselineLayerType;
  enabled: boolean;
  health: BaselineHealthType;
  /**
   * Every region the product's own plan puts a stack of this baseline in, in
   * the status read's order. Taken from the wanted rows, so a region shows here
   * before anything is installed in it.
   */
  placements: BaselinePlacement[];
  /** The wanted stack rows behind this baseline, one per stack and region. */
  stacks: BaselineStackView[];
  /** Why it is halted or waiting, in the backend's own words. */
  reason?: string;
}

const isWaiting = (s: BaselineStackView) =>
  s.status === BaselineStackStatus.PENDING && WAITING_REASONS.has(s.blocked_reason ?? '');

/** Worst first: the first state any stack is in decides the baseline's. */
const ORDER: Array<[BaselineHealthType, (s: BaselineStackView) => boolean]> = [
  [BaselineHealth.REFUSED, (s) => s.status === BaselineStackStatus.REFUSED],
  [BaselineHealth.BLOCKED, (s) => s.status === BaselineStackStatus.BLOCKED],
  [BaselineHealth.WAITING_ON_DEPENDENCY, isWaiting],
  [BaselineHealth.APPLYING, (s) => s.status === BaselineStackStatus.APPLYING || s.status === BaselineStackStatus.PENDING],
  [BaselineHealth.MISSING, (s) => s.status === BaselineStackStatus.NOT_STARTED],
];

function rollUp(stacks: BaselineStackView[]): Pick<BaselineSummary, 'health' | 'reason'> {
  if (stacks.length === 0) return { health: BaselineHealth.MISSING };
  for (const [health, matches] of ORDER) {
    const first = stacks.find(matches);
    if (first) {
      const explained = health === BaselineHealth.REFUSED
        || health === BaselineHealth.BLOCKED
        || health === BaselineHealth.WAITING_ON_DEPENDENCY;
      return { health, reason: explained ? stackReason(first) : undefined };
    }
  }
  return { health: BaselineHealth.IN_SYNC };
}

/** Every baseline of one account, in the order the status read lists them. */
export function summarizeBaselines(account: AccountBaselineView): BaselineSummary[] {
  const stacks = (account.stacks ?? []).filter((s) => s.desired);
  return (account.baselines ?? []).map((b) => {
    const own = stacks.filter((s) => s.baseline === b.name);
    const optionalOff = b.layer === BaselineLayer.OPTIONAL && !b.enabled;
    const placements = [...new Set(own.map((s) => s.region))].map((region) => ({
      region,
      main: region === account.main_region,
      health: rollUp(own.filter((s) => s.region === region)).health,
    }));
    return {
      name: b.name,
      title: TITLES[b.name] ?? humanize(b.name),
      layer: b.layer,
      enabled: b.enabled,
      placements,
      stacks: own,
      ...(optionalOff ? { health: BaselineHealth.OFF } : rollUp(own)),
    };
  });
}

/** How one placement reads on the card: its state in words, which a screen reader says as written. */
const PLACEMENT_WORDS: Record<BaselineHealthType, string> = {
  IN_SYNC: 'in sync',
  APPLYING: 'pending',
  MISSING: 'not started',
  REFUSED: 'refused',
  BLOCKED: 'blocked',
  WAITING_ON_DEPENDENCY: 'waiting',
  OFF: 'off',
};

/** "eu-central-1 (main) in sync, us-east-1 pending". */
export function placementLine(placements: BaselinePlacement[]): string {
  return placements
    .map((p) => `${p.region}${p.main ? ' (main)' : ''} ${PLACEMENT_WORDS[p.health]}`)
    .join(', ');
}
